import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { debounce } from "es-toolkit/function"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Button } from "@/components/ui/button"
import { DetailCheckbox } from "@/components/ui/checkbox"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { Form, FormError, FormField } from "@/components/ui/form"
import { TextArea } from "@/components/ui/input"
import { ListRadioGroup } from "@/components/ui/list"
import { SearchContent, SearchInput, SearchItem, SuggestionSearch } from "@/components/ui/search"
import { useClickAway } from "@/hooks/useClickAway"
import { toast } from "@/hooks/useToast"
import { GenderIcon } from "@/modules/patients/components/gender-icon"
import {
  DuplicatePatient,
  ManageDuplicatesPatientItem,
} from "@/modules/patients/manage-duplicates/manage-duplicates-patient-item"
import {
  ManageDuplicatesForm,
  manageDuplicatesSchema,
  REASON_MAX_LENGTH,
} from "@/modules/patients/manage-duplicates/manage-duplicates-schema"
import type { findChPatientByInternalIdQuery$data } from "@/modules/patients/manage-duplicates/services/ch/__generated__/findChPatientByInternalIdQuery.graphql"
import { findChPatientByInternalId } from "@/modules/patients/manage-duplicates/services/ch/findChPatientByInternalId"
import { findDePatientByInternalId } from "@/modules/patients/manage-duplicates/services/de/findDePatientByInternalId"
import { useConfirmDuplicatePatientsMutation } from "@/modules/patients/manage-duplicates/services/useConfirmDuplicatePatientsMutation"
import { useDuplicatePatientByIdQuery } from "@/modules/patients/manage-duplicates/services/useDuplicatePatientByIdQuery"
import { useDuplicatePatientChangedSubscription } from "@/modules/patients/manage-duplicates/services/useDuplicatePatientChangedSubscription"
import { useUndoDuplicatePatientsMutation } from "@/modules/patients/manage-duplicates/services/useUndoDuplicatePatientsMutation"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { Country } from "@/types/__generated__/union-types"

type SearchResult = NonNullable<findChPatientByInternalIdQuery$data["ch_patientByInternalId"]>

type SearchState = {
  value: string
  error: string
  isLoading: boolean
  results: SearchResult[]
  hasSearched: boolean
}

const initialSearchState: SearchState = {
  value: "",
  error: "",
  isLoading: false,
  results: [],
  hasSearched: false,
}

const fetchPatientByInternalId = async (country: Country, internalId: number): Promise<SearchResult[]> => {
  const patient =
    country === "CH"
      ? (await findChPatientByInternalId(internalId))?.ch_patientByInternalId
      : (await findDePatientByInternalId(internalId))?.de_patientByInternalId
  return patient ? [patient] : []
}

type ManageDuplicatesDrawerProps = {
  country: Country
  initialPatientId: string
  isEditMode?: boolean
  onPatientUpdated?: () => void
}

const ManageDuplicatesDrawer = ({
  country,
  initialPatientId,
  isEditMode = false,
  onPatientUpdated,
}: ManageDuplicatesDrawerProps) => {
  const { patient: initialPatient, duplicatePatients: initialDuplicates } = useDuplicatePatientByIdQuery(
    country,
    initialPatientId,
  )
  const { t } = useTranslation()
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const closeDrawerWithConfirmation = useDrawerStore((state) => state.closeDrawerWithConfirmation)

  const initialPatients = useMemo(() => {
    if (!initialPatient) return []
    const duplicatesWithCountry = initialDuplicates.map((d) => ({ ...d, country }))
    return [initialPatient, ...duplicatesWithCountry]
  }, [initialPatient, initialDuplicates, country])

  const [patients, setPatients] = useState<DuplicatePatient[]>(initialPatients)
  const [undoDuplicatePatientIds, setUndoDuplicatePatientIds] = useState<string[]>([])
  const [search, setSearch] = useState<SearchState>(initialSearchState)

  const resetSearch = () => setSearch(initialSearchState)
  const searchResultsRef = useClickAway(resetSearch)

  const form = useForm<ManageDuplicatesForm>({
    resolver: zodResolver(manageDuplicatesSchema),
    mode: "all",
    defaultValues: {
      patientIds: initialPatients.map((p) => p.id),
      primaryPatientId: isEditMode ? initialPatientId : "",
      reason: "",
      confirmed: false,
    },
  })

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    register,
    formState: { isDirty, isSubmitted, errors },
  } = form

  const primaryPatientId = watch("primaryPatientId")

  const [confirmDuplicates, isConfirmingInFlight] = useConfirmDuplicatePatientsMutation(country)
  const [undoDuplicates, isUndoInFlight] = useUndoDuplicatePatientsMutation(country)
  const confirm = useConfirmStore((state) => state.confirm)

  const patientIds = patients.map((p) => p.id)
  const handlePatientChanged = (updatedPatient: DuplicatePatient) => {
    setPatients((prev) => prev.map((p) => (p.id === updatedPatient.id ? { ...p, ...updatedPatient } : p)))
  }
  useDuplicatePatientChangedSubscription(country, patientIds, handlePatientChanged)

  const hasAppChannelIssue =
    patients.length >= 2 &&
    !!primaryPatientId &&
    patients.some((p) => p.id !== primaryPatientId && p.contactChannel === "APP")

  const handleClose = () => {
    if (isDirty) {
      closeDrawerWithConfirmation()
    } else {
      closeDrawer()
    }
  }

  const handleDeleteRelations = async () => {
    const confirmed = await confirm({
      title: t("patient.duplicates.deleteRelations.title"),
      content: <p>{t("patient.duplicates.deleteRelations.content")}</p>,
      confirmText: t("patient.duplicates.deleteRelations.confirmButton"),
      cancelText: t("common.button.cancel"),
    })

    if (confirmed) {
      undoDuplicates({ primaryPatientId: initialPatientId }, () => {
        window.disableConfirmBeforeLeaving = true
        window.location.reload()
      })
    }
  }

  useDirtyDrawerMaker(isDirty, handleClose)

  const debouncedSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        const pid = parseInt(value, 10)

        const alreadyAdded = patients.find((p) => p.displayId === pid)
        if (alreadyAdded) {
          setSearch((prev) => ({ ...prev, error: t("patient.duplicates.search.alreadyAdded") }))
          return
        }

        setSearch((prev) => ({ ...prev, error: "", results: [], hasSearched: false, isLoading: true }))

        try {
          const nodes = await fetchPatientByInternalId(country, pid)
          setSearch((prev) => ({ ...prev, results: [...nodes], hasSearched: true }))
        } catch {
          setSearch((prev) => ({ ...prev, error: t("patient.duplicates.search.failed") }))
        } finally {
          setSearch((prev) => ({ ...prev, isLoading: false }))
        }
      }, 500),
    [country, patients, t],
  )

  const addPatientsToList = (newPatients: DuplicatePatient[]) => {
    setPatients((prev) => {
      const existingIds = new Set(prev.map((p) => p.id))
      const uniqueNew = newPatients.filter((p) => !existingIds.has(p.id))
      if (uniqueNew.length === 0) return prev
      const updated = [...prev, ...uniqueNew]
      setValue(
        "patientIds",
        updated.map((p) => p.id),
        { shouldValidate: true, shouldDirty: true },
      )
      return updated
    })
  }

  const handleAddPatient = (result: SearchResult) => {
    const patientsToAdd: DuplicatePatient[] = [result]
    if (result.duplicatePatients.length > 0) {
      const duplicatesWithCountry = result.duplicatePatients.map((d) => ({ ...d, country }))
      patientsToAdd.push(...duplicatesWithCountry)
    }
    addPatientsToList(patientsToAdd)
    resetSearch()
  }

  const handleRemovePatient = (patientId: string) => {
    setPatients((prev) => {
      const updated = prev.filter((p) => p.id !== patientId)
      setValue(
        "patientIds",
        updated.map((p) => p.id),
      )
      return updated
    })

    if (primaryPatientId === patientId) {
      setValue("primaryPatientId", "")
    }

    setUndoDuplicatePatientIds((prev) => [...prev, patientId])
  }

  const handleOpenRecord = (patientId: string, patientCountry: Country) => {
    window.open(`/patients/${patientCountry.toLowerCase()}/${patientId}`, "_blank")
  }

  const onSubmit = handleSubmit((data) => {
    if (hasAppChannelIssue) return
    const uniqueUndoIds = [...new Set(undoDuplicatePatientIds)]
    const duplicatePatientIds = data.patientIds.filter(
      (id) => id !== data.primaryPatientId && !uniqueUndoIds.includes(id),
    )

    confirmDuplicates(
      {
        primaryPatientId: data.primaryPatientId,
        duplicatePatientIds,
        undoDuplicatePatientIds: uniqueUndoIds,
        reason: data.reason ?? "",
      },
      () => {
        toast({ description: t("patient.duplicates.toast.success") })
        if (isEditMode) {
          window.disableConfirmBeforeLeaving = true
          window.location.reload()
        } else {
          onPatientUpdated?.()
        }
        closeDrawer()
      },
    )
  })

  return (
    <Drawer open onOpenChange={handleClose}>
      <DrawerContent className="shadow-1 relative border" data-testid="manage-duplicates-drawer" hasAnimation={false}>
        <DrawerClose className="top-2 right-2" />
        <DrawerHeader>
          <DrawerTitle className="text-on-surface-subdued text-sm font-medium" data-testid="manage-duplicates-title">
            {t("patient.duplicates.title")}
          </DrawerTitle>
        </DrawerHeader>

        <DrawerBody className="group/scrollbar grid grid-rows-1 overflow-hidden p-0">
          <ScrollWrapper>
            <Form {...form}>
              <div className="flex flex-col gap-4 p-4">
                <div className="flex flex-col gap-1">
                  <span className="text-on-surface text-base font-medium">
                    {t("patient.duplicates.selectDuplicatePatients")}
                  </span>
                  <span className="text-on-surface-subdued text-sm whitespace-pre-line">
                    {t("patient.duplicates.subtitle")}
                  </span>
                </div>

                <SuggestionSearch data-testid="duplicate-search">
                  <SearchInput
                    placeholder={t("patient.duplicates.search.placeholder")}
                    value={search.value}
                    isLoading={search.isLoading}
                    error={!!search.error}
                    dataTestId="duplicate"
                    autoFocus={false}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "")
                      setSearch({ ...initialSearchState, value })
                      if (value) {
                        debouncedSearch(value)
                      }
                    }}
                    onClear={resetSearch}
                  />
                  {search.error && <FormError className="mt-2">{search.error}</FormError>}
                  {search.hasSearched && (
                    <SearchContent ref={searchResultsRef} className="p-2" data-testid="duplicate-search-results">
                      {search.results.length === 0 ? (
                        <p className="text-on-surface-subdued px-4 py-2">{t("common.nothingFound")}</p>
                      ) : (
                        search.results.map((result) => (
                          <SearchItem
                            key={result.id}
                            className="flex items-center gap-2 px-2 py-1.5"
                            onSelect={() => handleAddPatient(result)}
                            data-testid={`duplicate-search-result-${result.displayId}`}>
                            <GenderIcon gender={result.gender} className="h-10 w-10 min-w-10" />
                            <div>
                              <p className="text-on-surface text-sm">
                                {result.firstName} {result.lastName}
                              </p>
                              <p className="text-on-surface-subdued text-xs tracking-wide">
                                {result.displayId}
                                {result.contactChannel
                                  ? ` · ${t(`patient.information.contact.${result.contactChannel}`)}`
                                  : ""}
                              </p>
                            </div>
                          </SearchItem>
                        ))
                      )}
                    </SearchContent>
                  )}
                </SuggestionSearch>

                <FormField
                  control={control}
                  name="primaryPatientId"
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <div>
                      <ListRadioGroup
                        className="space-y-0.5"
                        value={value}
                        onValueChange={onChange}
                        data-testid="duplicate-patient-list">
                        {patients.map((patient) => (
                          <ManageDuplicatesPatientItem
                            key={patient.id}
                            patient={patient}
                            canRemove={isEditMode || patient.id !== initialPatient?.id}
                            hasError={
                              isSubmitted &&
                              hasAppChannelIssue &&
                              patient.id !== value &&
                              patient.contactChannel === "APP"
                            }
                            onRemove={() => handleRemovePatient(patient.id)}
                            onOpenRecord={() => handleOpenRecord(patient.id, patient.country)}
                          />
                        ))}
                      </ListRadioGroup>
                      {errors.patientIds?.message ? (
                        <FormError className="mt-2">{t(errors.patientIds.message)}</FormError>
                      ) : (
                        error?.message && <FormError className="mt-2">{t(error.message)}</FormError>
                      )}
                    </div>
                  )}
                />

                {isSubmitted && hasAppChannelIssue && (
                  <FormError>{t("patient.duplicates.errors.appCommunicationCritical")}</FormError>
                )}

                <div className="flex flex-col gap-4">
                  <p className="text-on-surface text-base font-medium">{t("patient.duplicates.reason.title")}</p>
                  <TextArea
                    {...register("reason")}
                    label={t("patient.duplicates.reason.label")}
                    error={errors.reason?.message}
                    translateError
                    data-testid="duplicate-reason"
                    textLimit={REASON_MAX_LENGTH}
                  />
                </div>

                <FormField
                  control={control}
                  name="confirmed"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex flex-col gap-2">
                      <p className="text-on-surface-subdued text-sm">{t("patient.duplicates.confirmation.label")}*</p>
                      <DetailCheckbox
                        checked={value === true}
                        error={!!errors.confirmed}
                        onCheckedChange={(checked) => {
                          onChange(checked === true)
                        }}
                        testId="duplicate-confirmation-checkbox">
                        {t("patient.duplicates.confirmation.checkbox")}
                      </DetailCheckbox>
                      {errors.confirmed?.message && <FormError>{t(errors.confirmed.message)}</FormError>}
                    </div>
                  )}
                />
              </div>
            </Form>
          </ScrollWrapper>
        </DrawerBody>

        <DrawerFooter className="sticky inset-x-0 bottom-0 justify-end gap-2">
          {isEditMode && (
            <Button
              variant="circle"
              className="size-10"
              onClick={handleDeleteRelations}
              disabled={isUndoInFlight}
              data-testid="delete-duplicate-relations-btn">
              <TrashSimple />
            </Button>
          )}
          <Button variant="secondary" onClick={handleClose} data-testid="duplicate-cancel-btn">
            {t("common.button.cancel")}
          </Button>
          <Button onClick={onSubmit} disabled={isConfirmingInFlight} data-testid="duplicate-save-btn">
            {t("common.button.save")}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export { ManageDuplicatesDrawer }
